import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCloneChecklist } from '@area-restrita/api-client/endpoints/hang-tag/hang-tag.gen';

import InlineInput from '@/components/InlineInput';
import { Icons } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Form, FormField } from '@/components/ui/form';
import { useDialog } from '@/components/template/Dialog';
import { cn } from '@/lib/utils';

const schema = z.object({
  name: z.string().min(1),
});

type Schema = z.infer<typeof schema>;

interface IProps {
  originalId: number;
  originalName: string;
}

const CloneCheckList: React.FC<IProps> = ({ originalId, originalName }) => {
  const { handleClose } = useDialog();

  const form = useForm<Schema>({
    defaultValues: { name: `Cópia de ${originalName}` },
    resolver: zodResolver(schema),
  });

  const cloneChecklist = useCloneChecklist({
    mutation: {
      onSuccess: () => {
        toast.success('Checklist copiado com sucesso');
        handleClose();
      },
      onError: () => {
        toast.error('Erro ao copiar o checklist');
      },
    },
  });

  const onSubmit = useCallback<SubmitHandler<Schema>>(
    async (data) => {
      await cloneChecklist.mutateAsync({
        data: {
          name: data.name,
          checklistId: originalId,
        },
      });
    },
    [cloneChecklist, originalId],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className={cn('grid gap-4 py-4')}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <InlineInput
                title="Novo Nome"
                value={field.value}
                onChange={field.onChange}
                placeholder={`Cópia de ${originalName}`}
              />
            )}
          />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting && (
              <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
            )}
            Copiar Check List
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default CloneCheckList;
